import { CSSProperties, memo } from 'react';

import { useWatch } from 'react-hook-form';

import { useProposalStore } from '@store/proposal';
import { Box } from '@ui';

import { AdaptiveFieldByType } from './AdaptiveFieldByType';
import { AdaptiveFormFieldConfig } from './types';

interface ConditionalFieldProps {
  config: AdaptiveFormFieldConfig;
  context?: any;
  wrapperStyle?: CSSProperties;
}

/**
 * Wrapper component that handles conditional rendering for individual fields.
 * Uses useWatch at the field level instead of at the AdaptiveFormRows level
 * to prevent unnecessary re-renders of the entire form.
 */
export const ConditionalField = memo(
  ({ config, context, wrapperStyle }: ConditionalFieldProps) => {
    const productDetails = useProposalStore((state) => state.productDetails);
    const watchForChanges = useProposalStore(
      (state) => state.currentStep?.watchForChanges
    );

    // Only watch if this field has a condition
    const watchedFields = useWatch({
      name: watchForChanges as any,
      disabled: !config.condition
    });

    // Evaluate condition if present
    if (typeof config.condition === 'function') {
      const isVisible = config.condition(watchedFields, productDetails);
      if (!isVisible) {
        return null;
      }
    }

    const content = <AdaptiveFieldByType config={config} context={context} />;

    // If wrapper style provided, wrap in Box
    if (wrapperStyle) {
      return <Box style={wrapperStyle}>{content}</Box>;
    }

    return content;
  }
);

ConditionalField.displayName = 'ConditionalField';
